import { Button, Toast, type ToastState } from '@alison/ui';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { TRPCClientError } from '@trpc/client';
import { type ReactNode, useEffect, useState } from 'react';
import { InviteForm, MembersTable, SettingsCard } from '@/components/settings';
import { useTRPC } from '@/lib/trpc.js';

function isForbiddenError(error: unknown): boolean {
  return error instanceof TRPCClientError && error.data?.code === 'FORBIDDEN';
}

function TeamCard({
  isAdmin,
  viewerIsAlisonStaff,
}: {
  isAdmin: boolean;
  viewerIsAlisonStaff: boolean;
}): ReactNode {
  const [inviteFormOpen, setInviteFormOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {isAdmin ? (
        <SettingsCard
          title="Team"
          subtitle="Manage who has access to this workspace and which integrations they can see."
          action={
            <Button
              variant="gradient"
              data-testid="button-invite-user"
              onClick={() => setInviteFormOpen(true)}
            >
              Invite user
            </Button>
          }
        >
          {inviteFormOpen ? (
            <InviteForm
              onClose={() => setInviteFormOpen(false)}
              onInvited={(email) => {
                setInviteFormOpen(false);
                setToast({ message: `Invitation sent to ${email}`, severity: 'success' });
              }}
              onError={(message) => setToast({ message, severity: 'error' })}
            />
          ) : null}
        </SettingsCard>
      ) : null}

      <SettingsCard
        title="Members"
        subtitle="Everyone with access to this workspace, including pending invites."
      >
        <MembersTable
          isAdmin={isAdmin}
          viewerIsAlisonStaff={viewerIsAlisonStaff}
          onToast={setToast}
        />
      </SettingsCard>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </Box>
  );
}

function UsersAndAccessPage(): ReactNode {
  const navigate = useNavigate();
  const trpc = useTRPC();

  const meQuery = useQuery(trpc.me.get.queryOptions());
  const membersQuery = useQuery({
    ...trpc.users.list.queryOptions(),
    retry: false,
  });

  useEffect(() => {
    if (isForbiddenError(membersQuery.error)) {
      void navigate({ to: '/dashboard' });
    }
  }, [membersQuery.error, navigate]);

  if (membersQuery.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isForbiddenError(membersQuery.error)) {
    return null;
  }

  return (
    <TeamCard
      isAdmin={meQuery.data?.isAdmin ?? false}
      viewerIsAlisonStaff={meQuery.data?.isAlisonStaff ?? false}
    />
  );
}

export const Route = createFileRoute('/settings/users/')({
  component: UsersAndAccessPage,
});
